import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
  Checkbox,
  FormGroup,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { formatAppDate } from '../../utils/dateFormat';
import { Link as RouterLink } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';
import { authApi, cabinetSettingsApi, cabinetUsersApi, yandexSubmittedViewsApi } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { OrdClient, OrdProvider, CabinetTariff, StartPricingMode } from '../../types';

const providerLabel: Record<OrdProvider, string> = {
  VK: 'VK ОРД',
  YANDEX: 'Яндекс ОРД',
  OZON: 'Ozon ОРД',
};

const providerHint: Record<OrdProvider, string> = {
  VK: 'VK ОРД работает с обычным токеном кабинета VK ORD. Media можно загружать через раздел media, а в креативах использовать загруженные media id.',
  OZON: 'Ozon ОРД использует Bearer token кабинета Ozon ORD. Media загружаются через раздел media, после чего их id можно указывать в креативе.',
  YANDEX: 'Яндекс ОРД использует Bearer token кабинета Yandex ORD. Для media нужен публичный URL файла; обычная загрузка файла в media для Яндекса не используется.',
};

const providerInstructionTitle: Record<OrdProvider, string> = {
  VK: 'Как работает VK ОРД',
  OZON: 'Как работает Ozon ОРД',
  YANDEX: 'Как работает Яндекс ОРД',
};

const providerInstructions: Record<OrdProvider, string[]> = {
  VK: [
    'Укажите токен вашего кабинета VK ОРД в поле «Токен ОРД».',
    'В разделе media можно загружать файлы напрямую, система отправит их в VK ОРД и сохранит media id.',
    'В креативе можно использовать загруженные media id, обычные target URL и тексты.',
    'На тарифе «Старт» используется аккаунт Тренди, поэтому отдельный токен клиента не требуется.',
  ],
  OZON: [
    'Укажите Bearer token вашего кабинета Ozon ОРД.',
    'Файлы media загружаются через раздел media и получают id внутри Ozon ОРД.',
    'В креативе используйте загруженные media id, target URL и текстовые материалы.',
    'Акты и статистика по Ozon отправляются автоматически по выбранному провайдеру кабинета.',
  ],
  YANDEX: [
    'Укажите Bearer token вашего кабинета Yandex ORD.',
    'Для media не нужен upload в систему: в креатив вставляется публичная ссылка на файл.',
    'Можно использовать публичную ссылку Яндекс Диска вида disk.yandex.ru/i/... — система сама запросит временный download href и подставит его в mediaUrl при отправке креатива.',
    'Ссылки вида disk.yandex.ru/client/disk/... не подходят, потому что работают только внутри авторизованного кабинета.',
    'Если статистику в Яндекс ОРД уже сдавали до Тренди — обязательно один раз загрузите Excel в блоке «Уже сданные показы» ниже (иначе в ОРД уйдёт полный накопительный счётчик вместо прироста).',
  ],
};

const START_PACKAGE_SIZE = 31;
const START_PACKAGE_PRICE = 1000;
const START_CREATIVE_PRICE = 40;
const BUSINESS_MONTHLY_PRICE = 3000;
const BUSINESS_LITE_PRICE = 1500;
const START_MAX_TOTAL_CREATIVES = 588;
const START_MAX_PACKAGES = Math.floor(START_MAX_TOTAL_CREATIVES / START_PACKAGE_SIZE);
const START_MAX_SINGLE_CREATIVES = START_PACKAGE_SIZE - 1;

const SettingsPage: React.FC = () => {
  const { user, refreshMe, isClientAdmin, isOwner } = useAuth();
  const [settings, setSettings] = useState<OrdClient | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [staffList, setStaffList] = useState<any[]>([]);
  const [staffLoading, setStaffLoading] = useState(false);
  const [staffName, setStaffName] = useState('');
  const [staffPerms, setStaffPerms] = useState({
    accessCreatives: false,
    accessPersons: false,
    accessContracts: true,
    accessInvoices: true,
  });
  const [staffSaving, setStaffSaving] = useState(false);
  const [staffCreds, setStaffCreds] = useState<{ login: string; password: string; personName: string } | null>(null);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newLogin, setNewLogin] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [ordProvider, setOrdProvider] = useState<OrdProvider>('VK');
  const [ordToken, setOrdToken] = useState('');
  const [autoReportEnabled, setAutoReportEnabled] = useState(true);
  const [testModeEnabled, setTestModeEnabled] = useState(false);
  const [dzenSubmissionEnabled, setDzenSubmissionEnabled] = useState(false);
  const [startPricingMode, setStartPricingMode] = useState<StartPricingMode>('PACKAGES');
  const [startPricingQuantity, setStartPricingQuantity] = useState<number>(1);
  const [renewTariff, setRenewTariff] = useState<CabinetTariff>('START');
  const [renewMode, setRenewMode] = useState<StartPricingMode>('PACKAGES');
  const [renewQuantity, setRenewQuantity] = useState(1);
  const [yandexImporting, setYandexImporting] = useState(false);
  const yandexFileRef = React.useRef<HTMLInputElement>(null);

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await cabinetSettingsApi.get();
      setSettings(data);
      if (data) {
        setOrdProvider(data.ordProvider || 'VK');
        setAutoReportEnabled(data.autoReportEnabled !== false);
        setTestModeEnabled(!!data.testModeEnabled);
        setDzenSubmissionEnabled(!!data.dzenSubmissionEnabled);
        if (data.startPricingMode) {
          setStartPricingMode(data.startPricingMode);
        }
        if (data.startPricingQuantity) {
          setStartPricingQuantity(data.startPricingQuantity);
        }
        setRenewTariff(data.tariff || 'START');
        if (data.startPricingMode) {
          setRenewMode(data.startPricingMode);
        }
        if (data.startPricingQuantity) {
          setRenewQuantity(data.startPricingQuantity);
        }
      }
    } catch (e: any) {
      setError(e?.message || 'Ошибка загрузки');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const loadStaff = async () => {
    setStaffLoading(true);
    try {
      setStaffList(await cabinetUsersApi.list());
    } catch {
      setStaffList([]);
    } finally {
      setStaffLoading(false);
    }
  };

  useEffect(() => {
    if (isClientAdmin) {
      loadStaff();
    }
  }, [isClientAdmin]);

  const createStaff = async () => {
    if (!staffName.trim()) {
      setError('Укажите имя сотрудника');
      return;
    }
    if (!staffPerms.accessCreatives && !staffPerms.accessPersons && !staffPerms.accessContracts && !staffPerms.accessInvoices) {
      setError('Выберите хотя бы один раздел');
      return;
    }
    setStaffSaving(true);
    setError(null);
    try {
      const res = await cabinetUsersApi.create({
        displayName: staffName.trim(),
        ...staffPerms,
      });
      setStaffCreds({ login: res.login, password: res.password, personName: res.personName });
      setStaffName('');
      await loadStaff();
      setSuccess('Сотрудник создан');
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Не удалось создать сотрудника');
    } finally {
      setStaffSaving(false);
    }
  };

  const revokeStaff = async (userId: number) => {
    if (!window.confirm('Отозвать доступ сотрудника?')) return;
    try {
      await cabinetUsersApi.revoke(userId);
      await loadStaff();
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Не удалось отозвать доступ');
    }
  };

  const saveCredentials = async () => {
    if (!currentPassword) {
      setError('Укажите текущий пароль');
      return;
    }
    if (!newLogin.trim() && !newPassword) {
      setError('Укажите новый логин или пароль');
      return;
    }
    try {
      setSaving(true);
      setError(null);
      await authApi.changeCredentials({
        currentPassword,
        newLogin: newLogin.trim() || undefined,
        newPassword: newPassword || undefined,
      });
      setCurrentPassword('');
      setNewLogin('');
      setNewPassword('');
      setSuccess('Учётные данные обновлены');
      await refreshMe();
    } catch (e: any) {
      setError(e?.message || 'Ошибка смены учётных данных');
    } finally {
      setSaving(false);
    }
  };

  const saveSettings = async () => {
    try {
      setSaving(true);
      setError(null);
      const updated = await cabinetSettingsApi.update(
        isOwner
          ? { dzenSubmissionEnabled }
          : {
              ordProvider: settings?.tariff === 'BUSINESS' ? ordProvider : undefined,
              ordToken: settings?.tariff === 'BUSINESS' ? ordToken : undefined,
              autoReportEnabled,
              testModeEnabled,
              dzenSubmissionEnabled,
            }
      );
      setSettings(updated);
      setOrdToken('');
      setSuccess('Настройки сохранены');
      await refreshMe();
    } catch (e: any) {
      setError(e?.message || 'Ошибка сохранения');
    } finally {
      setSaving(false);
    }
  };

  const subscriptionActive = user?.subscriptionActive !== false;
  const tariffName = settings?.tariff === 'BUSINESS'
    ? (settings.statsSubmissionEnabled === false ? 'Бизнес (без статистики)' : 'Бизнес')
    : 'Старт';
  const effectiveProvider: OrdProvider = settings?.tariff === 'BUSINESS' ? ordProvider : 'VK';
  const showYandexSubmittedImport =
    settings?.tariff === 'BUSINESS' && (settings?.ordProvider || ordProvider) === 'YANDEX';

  const importYandexSubmittedViews = async (file: File | null) => {
    if (!file) return;
    try {
      setYandexImporting(true);
      setError(null);
      const result = await yandexSubmittedViewsApi.importSubmittedViews(file);
      setSuccess(
        `Импорт уже сданных показов: сохранено ${result.savedCount}, без пары ${result.unmatchedCount}`
        + (result.skippedCount ? `, пропущено ${result.skippedCount}` : '')
      );
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Ошибка импорта Excel');
    } finally {
      setYandexImporting(false);
      if (yandexFileRef.current) yandexFileRef.current.value = '';
    }
  };
  const businessPrice = settings?.statsSubmissionEnabled === false ? BUSINESS_LITE_PRICE : BUSINESS_MONTHLY_PRICE;
  const startQuota = settings?.startQuota;
  const billingDay = startQuota?.billingDayOfMonth ?? 7;
  const clampedStartQuantity = Math.max(
    1,
    Math.min(
      startPricingQuantity || 1,
      startPricingMode === 'PACKAGES' ? START_MAX_PACKAGES : START_MAX_SINGLE_CREATIVES
    )
  );
  const startCalculatedPrice = startPricingMode === 'PACKAGES'
    ? clampedStartQuantity * START_PACKAGE_PRICE
    : clampedStartQuantity * START_CREATIVE_PRICE;
  const planLabel = startPricingMode === 'PACKAGES'
    ? `${clampedStartQuantity} пак. × ${START_PACKAGE_SIZE} креативов`
    : `${clampedStartQuantity} креативов поштучно`;

  const renewMaxQuantity = renewMode === 'PACKAGES' ? START_MAX_PACKAGES : START_MAX_SINGLE_CREATIVES;
  const clampedRenewQuantity = Math.min(Math.max(renewQuantity || 1, 1), renewMaxQuantity);
  const renewPrice = renewTariff === 'BUSINESS'
    ? businessPrice
    : renewMode === 'PACKAGES'
      ? clampedRenewQuantity * START_PACKAGE_PRICE
      : clampedRenewQuantity * START_CREATIVE_PRICE;
  const renewPriceLabel = renewTariff === 'BUSINESS'
    ? `${businessPrice.toLocaleString('ru-RU')} ₽ / мес${settings?.statsSubmissionEnabled === false ? ' (без статистики)' : ''}`
    : renewMode === 'PACKAGES'
      ? `${renewPrice.toLocaleString('ru-RU')} ₽ за ${clampedRenewQuantity} пак. (${clampedRenewQuantity * START_PACKAGE_SIZE} креативов)`
      : `${renewPrice.toLocaleString('ru-RU')} ₽ за ${clampedRenewQuantity} креативов`;

  const handlePayClick = () => {
    setSuccess(
      `К оплате: ${renewPriceLabel}. Онлайн-оплата скоро будет подключена — напишите нам, и мы продлим подписку вручную.`
    );
  };

  return (
    <Box>
      <PageHeader
        title="Настройки"
        description={user?.ordClientName ? `Кабинет: ${user.ordClientName}` : 'Параметры вашего кабинета'}
      />

      {error && <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 1.5 }} onClose={() => setSuccess(null)}>{success}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Stack spacing={2}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>Подписка</Typography>
            <Typography variant="body2">Тариф: <strong>{tariffName}</strong></Typography>
            {settings?.tariff === 'BUSINESS' && (
              <Typography variant="body2">
                Стоимость: <strong>{businessPrice.toLocaleString('ru-RU')} ₽/мес</strong>
                {settings.statsSubmissionEnabled === false
                  ? ' — статистику в ОРД сдаёте сами / через ЛК ОРД'
                  : ' — включая сдачу статистики'}
              </Typography>
            )}
            <Typography variant="body2">
              ОРД: <strong>{settings?.ordProvider ? providerLabel[settings.ordProvider] : 'VK ОРД (через Тренди)'}</strong>
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              Оплачено до:{' '}
              <strong>
                {settings?.subscriptionPaidUntil
                  ? formatAppDate(settings.subscriptionPaidUntil)
                  : 'без ограничения'}
              </strong>
            </Typography>
            {!subscriptionActive && (
              <Alert severity="warning" sx={{ mt: 1.5 }}>
                Подписка неактивна. Сдача статистики может быть ограничена.
              </Alert>
            )}
          </Paper>

          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5 }}>
              Продление подписки
            </Typography>
            <Stack spacing={2}>
              <FormControl fullWidth>
                <InputLabel>Тариф</InputLabel>
                <Select
                  label="Тариф"
                  value={renewTariff}
                  onChange={(e) => setRenewTariff(e.target.value as CabinetTariff)}
                >
                  <MenuItem value="START">Старт</MenuItem>
                  <MenuItem value="BUSINESS">Бизнес</MenuItem>
                </Select>
              </FormControl>
              {renewTariff === 'START' && (
                <>
                  <FormControl fullWidth>
                    <InputLabel>Модель оплаты</InputLabel>
                    <Select
                      label="Модель оплаты"
                      value={renewMode}
                      onChange={(e) => setRenewMode(e.target.value as StartPricingMode)}
                    >
                      <MenuItem value="PACKAGES">Пакеты (31 креатив)</MenuItem>
                      <MenuItem value="CREATIVES">Поштучно</MenuItem>
                    </Select>
                  </FormControl>
                  <TextField
                    label={renewMode === 'PACKAGES' ? 'Количество пакетов' : 'Количество креативов'}
                    type="number"
                    value={clampedRenewQuantity}
                    onChange={(e) => setRenewQuantity(Number(e.target.value) || 1)}
                    fullWidth
                    inputProps={{ min: 1, max: renewMaxQuantity }}
                    helperText={
                      renewMode === 'PACKAGES'
                        ? `1 пакет = ${START_PACKAGE_SIZE} креативов за ${START_PACKAGE_PRICE} ₽`
                        : `${START_CREATIVE_PRICE} ₽ за креатив, максимум ${START_MAX_SINGLE_CREATIVES}`
                    }
                  />
                </>
              )}
              {renewTariff === 'BUSINESS' && (
                <Alert severity="info">
                  Автоматизация вашего ОРД. Услуги самого ОРД оплачиваются отдельно.
                </Alert>
              )}
              <Paper variant="outlined" sx={{ p: 1.5 }}>
                <Typography variant="body2">
                  К оплате: <strong>{renewPriceLabel}</strong>
                </Typography>
              </Paper>
              <Button variant="contained" onClick={handlePayClick} disabled={saving}>
                Оплатить
              </Button>
              <Typography variant="caption" color="text.secondary">
                Онлайн-оплата будет подключена позже. Сейчас кнопка показывает сумму и оставляет заявку на ручное продление.
              </Typography>
            </Stack>
          </Paper>

          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5 }}>Учётная запись</Typography>
            <Stack spacing={2}>
              <TextField
                label="Текущий пароль"
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                fullWidth
              />
              <TextField
                label="Новый логин"
                value={newLogin}
                onChange={(e) => setNewLogin(e.target.value)}
                fullWidth
                placeholder={user?.login}
              />
              <TextField
                label="Новый пароль"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                fullWidth
              />
              <Button variant="contained" onClick={saveCredentials} disabled={saving}>
                {saving ? <CircularProgress size={20} /> : 'Сменить логин / пароль'}
              </Button>
            </Stack>
          </Paper>

          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5 }}>Кабинет</Typography>
            <Stack spacing={2}>
              {!isOwner && (
                <>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={testModeEnabled}
                        onChange={(e) => setTestModeEnabled(e.target.checked)}
                      />
                    }
                    label="Тестовый режим"
                  />
                  <Typography variant="body2" color="text.secondary" sx={{ mt: -1 }}>
                    Ручная сдача по одному креативу (просмотры, акт, статистика). Пока включён —
                    автосдача для кабинета не выполняется.
                  </Typography>
                  {testModeEnabled && (
                    <Alert severity="warning">
                      Тестовый режим активен: на странице «Креативы» появятся кнопки ручной сдачи
                      для креативов в статусе отчётности. Выключите режим, когда будете готовы к полной автоматизации.
                    </Alert>
                  )}
                  <FormControlLabel
                    control={
                      <Switch
                        checked={autoReportEnabled}
                        onChange={(e) => setAutoReportEnabled(e.target.checked)}
                        disabled={testModeEnabled}
                      />
                    }
                    label="Автосдача статистики"
                  />
                  {testModeEnabled && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: -1.5 }}>
                      Автосдача приостановлена, пока включён тестовый режим.
                    </Typography>
                  )}
                </>
              )}
              <FormControlLabel
                control={
                  <Switch
                    checked={dzenSubmissionEnabled}
                    onChange={(e) => setDzenSubmissionEnabled(e.target.checked)}
                  />
                }
                label="Сдавать Дзен"
              />
              <Typography variant="body2" color="text.secondary" sx={{ mt: -1 }}>
                Полуавтомат: раз в месяц загружаете отчёт из Дзен Студии. Выключите — пункт «Дзен»
                исчезнет из меню, поле ссылки в креативах тоже.
              </Typography>
              {dzenSubmissionEnabled && (
                <Alert severity="info">
                  <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>Как сдавать Дзен</Typography>
                  <Typography variant="body2" component="div">
                    1. В креативе прикрепите ссылку вида{' '}
                    <code>dzen.ru/video/watch/…</code>.
                    <br />
                    2. Студия → Статистика → Публикации → период → Отчёт ↓ (xlsx).
                    <br />
                    3. <strong>Первый раз:</strong> период с начала канала до последнего дня
                    предыдущего месяца (сдача в июле → до 30 июня).
                    <br />
                    4. <strong>Дальше ежемесячно:</strong> только предыдущий месяц (1–30 июня при сдаче в июле).
                    <br />
                    5. Опционально в столбце F вместо «Показы» укажите erid напротив строки — для сопоставления
                    со старой сдачей.
                    <br />
                    6. Загрузите файл на странице{' '}
                    <Button component={RouterLink} to="/cabinet/dzen" size="small" sx={{ p: 0, minWidth: 0, verticalAlign: 'baseline' }}>
                      Дзен
                    </Button>
                    — статистика уйдёт в ОРД сразу после загрузки.
                  </Typography>
                </Alert>
              )}
              {!isOwner && settings?.tariff === 'BUSINESS' && (
                <>
                  <FormControl fullWidth>
                    <InputLabel>ОРД</InputLabel>
                    <Select label="ОРД" value={ordProvider} onChange={(e) => setOrdProvider(e.target.value as OrdProvider)}>
                      <MenuItem value="VK">VK ОРД</MenuItem>
                      <MenuItem value="YANDEX">Яндекс ОРД</MenuItem>
                      <MenuItem value="OZON">Ozon ОРД</MenuItem>
                    </Select>
                  </FormControl>
                  <TextField
                    label="Токен ОРД"
                    type="password"
                    value={ordToken}
                    onChange={(e) => setOrdToken(e.target.value)}
                    fullWidth
                    helperText={
                      settings.hasOrdToken
                        ? 'Токен уже задан. Введите новый, чтобы заменить.'
                        : 'Укажите токен вашего ОРД'
                    }
                  />
                  <Alert severity={ordProvider === 'YANDEX' ? 'warning' : 'info'}>
                    {providerHint[ordProvider]}
                  </Alert>
                </>
              )}
              {!isOwner && settings?.tariff === 'START' && (
                <Alert severity="info">
                  На тарифе «Старт» работа с ОРД VK выполняется через аккаунт Тренди. Токен не требуется.
                </Alert>
              )}
              <Button variant="outlined" onClick={saveSettings} disabled={saving}>
                {saving ? <CircularProgress size={20} /> : 'Сохранить настройки'}
              </Button>
            </Stack>
          </Paper>

          {settings?.tariff === 'START' && (
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5 }}>
                Квота креативов «Старт»
              </Typography>
              <Stack spacing={2}>
                <Alert severity="warning">
                  Неиспользованные креативы сгорают в конце биллингового периода и не переносятся на следующий.
                  Период обновляется с {billingDay}-го числа каждого месяца (не по календарному месяцу).
                </Alert>
                {startQuota ? (
                  <Paper variant="outlined" sx={{ p: 1.5 }}>
                    <Stack spacing={0.75}>
                      <Typography variant="body2">
                        Текущий период:{' '}
                        <strong>
                          {formatAppDate(startQuota.periodStart)}
                          {' — '}
                          {formatAppDate(startQuota.periodEnd)}
                        </strong>
                      </Typography>
                      <Typography variant="body2">
                        Тарифный план: <strong>{planLabel}</strong>
                      </Typography>
                      <Typography variant="body2">
                        Использовано: <strong>{startQuota.creativesUsed ?? 0}</strong> из{' '}
                        <strong>{startQuota.creativeLimit ?? 0}</strong>
                      </Typography>
                      <Typography variant="body2">
                        Осталось: <strong>{startQuota.creativesRemaining ?? 0}</strong>
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Стоимость плана: {startCalculatedPrice.toLocaleString('ru-RU')} руб./период.
                        Для изменения лимита обратитесь к администратору Тренди.
                      </Typography>
                    </Stack>
                  </Paper>
                ) : (
                  <Alert severity="info">
                    Квота для текущего периода ещё не настроена. Обратитесь к администратору.
                  </Alert>
                )}
              </Stack>
            </Paper>
          )}

          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
              {providerInstructionTitle[effectiveProvider]}
            </Typography>
            <Stack spacing={1.25}>
              {providerInstructions[effectiveProvider].map((item, index) => (
                <Box key={index} sx={{ display: 'flex', gap: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    {index + 1}.
                  </Typography>
                  <Typography variant="body2">{item}</Typography>
                </Box>
              ))}
            </Stack>
          </Paper>

          {showYandexSubmittedImport && (
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
                Уже сданные показы (Яндекс ОРД)
              </Typography>
              <Alert severity="info" sx={{ mb: 1.5 }}>
                <Typography variant="body2" fontWeight={600} sx={{ mb: 0.75 }}>
                  Зачем это нужно
                </Typography>
                <Typography variant="body2" component="div" sx={{ mb: 1 }}>
                  В API Яндекс ОРД нельзя запросить историю уже сданной статистики.
                  Тренди знает только то, что само отправило (или что вы загрузили сюда).
                  Без базалайна при первой автосдаче в ОРД уйдёт <strong>текущее полное</strong> число
                  просмотров ролика — как будто раньше ничего не сдавали. Если показы уже были
                  в ЕРИР, получится дубль.
                </Typography>
                <Typography variant="body2" component="div">
                  После загрузки Excel мы сохраняем сумму уже сданных показов. Каждый месяц в ОРД
                  уходит только <strong>дельта</strong> (сейчас минус уже сданное), а сданное
                  прибавляется у нас для следующего месяца.
                </Typography>
              </Alert>
              <Typography variant="body2" fontWeight={600} sx={{ mb: 0.75 }}>
                Как подготовить файл
              </Typography>
              <Typography variant="body2" color="text.secondary" component="div" sx={{ mb: 1.5 }}>
                1. Откройте ЛК Яндекс ОРД → раздел «Статистика» и по каждому креативу сложите
                уже переданные показы (факт) за все прошлые периоды.
                <br />
                2. Создайте Excel (.xlsx): <strong>столбец A — erid</strong>,{' '}
                <strong>столбец B — сумма уже сданных показов</strong> (одно число на креатив).
                <br />
                3. Первая строка может быть заголовком («erid», «показы») — она пропускается.
                <br />
                4. Erid должен совпадать с креативом в этом кабинете (креатив уже в Тренди, со ссылками).
                <br />
                5. Нажмите «Загрузить Excel» ниже. Повторная загрузка по тому же erid{' '}
                <strong>заменяет</strong> всю сохранённую у нас историю сдач по этому креативу
                числом из файла.
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Пример: erid <code>2Vtz…</code>, в ОРД уже сдано суммарно 12&nbsp;000 показов →
                в файле строка <code>2Vtz… | 12000</code>. Если сейчас на ролике 15&nbsp;000,
                в ОРД уйдёт 3&nbsp;000; у нас сохранится 15&nbsp;000 как «уже сданные».
              </Typography>
              <input
                ref={yandexFileRef}
                type="file"
                accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                hidden
                onChange={(e) => importYandexSubmittedViews(e.target.files?.[0] || null)}
              />
              <Button
                variant="contained"
                disabled={yandexImporting}
                onClick={() => yandexFileRef.current?.click()}
              >
                {yandexImporting ? <CircularProgress size={20} /> : 'Загрузить Excel'}
              </Button>
            </Paper>
          )}

          {isClientAdmin && (
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
                Сотрудники
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Выдайте доступ бухгалтеру, монтажёру или другому сотруднику — только к нужным разделам.
              </Typography>
              <FormGroup sx={{ mb: 1.5 }}>
                <FormControlLabel control={<Checkbox checked={staffPerms.accessCreatives} onChange={(e) => setStaffPerms((p) => ({ ...p, accessCreatives: e.target.checked }))} />} label="Креативы" />
                <FormControlLabel control={<Checkbox checked={staffPerms.accessPersons} onChange={(e) => setStaffPerms((p) => ({ ...p, accessPersons: e.target.checked }))} />} label="Контрагенты" />
                <FormControlLabel control={<Checkbox checked={staffPerms.accessContracts} onChange={(e) => setStaffPerms((p) => ({ ...p, accessContracts: e.target.checked }))} />} label="Договоры" />
                <FormControlLabel control={<Checkbox checked={staffPerms.accessInvoices} onChange={(e) => setStaffPerms((p) => ({ ...p, accessInvoices: e.target.checked }))} />} label="Акты" />
              </FormGroup>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 2 }}>
                <TextField label="Имя / роль" value={staffName} onChange={(e) => setStaffName(e.target.value)} fullWidth size="small" placeholder="Бухгалтер" />
                <Button variant="contained" onClick={createStaff} disabled={staffSaving} sx={{ whiteSpace: 'nowrap' }}>
                  {staffSaving ? <CircularProgress size={20} /> : 'Создать доступ'}
                </Button>
              </Stack>
              {staffLoading ? (
                <CircularProgress size={22} />
              ) : staffList.length > 0 ? (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Логин</TableCell>
                      <TableCell>Имя</TableCell>
                      <TableCell align="right" />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {staffList.map((u) => (
                      <TableRow key={u.userId}>
                        <TableCell>{u.login}</TableCell>
                        <TableCell>{u.personName || '—'}</TableCell>
                        <TableCell align="right">
                          <Button size="small" color="error" onClick={() => revokeStaff(u.userId)}>Забрать</Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <Typography variant="body2" color="text.secondary">Сотрудников пока нет</Typography>
              )}
            </Paper>
          )}
        </Stack>
      )}

      <Dialog open={!!staffCreds} onClose={() => setStaffCreds(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Доступ для сотрудника</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1 }}><b>{staffCreds?.personName}</b></Typography>
          <Typography variant="body2">Логин: <b>{staffCreds?.login}</b></Typography>
          <Typography variant="body2">Пароль: <b>{staffCreds?.password}</b></Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStaffCreds(null)}>Закрыть</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SettingsPage;
